import axios from "./axiosInstance";
import config from "./config";
import { getAuthToken } from "./apiHelpers";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

export const registerStaff = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/auth/register`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

export const getUserDetail = (token, id, source) => {
  // Both central platform users AND tenant owners live in the central
  // user_profiles table with NO tenant `users` row — both must use the
  // /users/central endpoint, or /users/:id 404s for them. 2026-06-10.
  const endpoint =
    source === "central" || source === "tenant_owner"
      ? `${BASE_URL}/users/central/${id}`
      : `${BASE_URL}/users/${id}`;

  return axios
    .get(endpoint, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

export const getAllUsers = (token, { role, excludeRole, page, limit } = {}) => {
  const params = {};

  if (role) params.role = role;
  if (excludeRole) params.excludeRole = excludeRole;
  if (page) params.page = page;
  if (limit) params.limit = limit;
  return axios
    .get(`${BASE_URL}/users`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params,
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
export const getTenant = () => {
  return axios
    .get(`${BASE_URL}/auth/current-tenant`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// PUT
export const updateUser = (token, id, newForm) => {
  return axios
    .put(`${BASE_URL}/users/${id}`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// POST /users/:id/revoke-sessions — "Sign out everywhere". Ends every session
// for that user on every device; they must sign in again. Returns the response
// body ({ success, data: { user_id, sessions_valid_after } }).
export const revokeUserSessions = (token, id) => {
  return axios
    .post(`${BASE_URL}/users/${id}/revoke-sessions`, {}, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// DELETE /users/:id/revoke-sessions — undo "Sign out everywhere".
//
// Until 2026-09-16 there was no undo at all: the stamp could be set and never
// cleared, so a revocation barred the account until someone with database
// access intervened. Three Gymnastics of York staff sat locked out for two days
// that way. Signing in also clears it now; this is for when you revoked the
// wrong person, or their client is stuck replaying a dead token.
export const restoreUserSessions = (token, id) => {
  return axios
    .delete(`${BASE_URL}/users/${id}/revoke-sessions`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// PATCH /users/me/avatar — self-service avatar update (server only ever
// touches the caller's own row). Replaces the old direct Supabase
// users.update write. Pass null/"" to clear. Returns the new avatar_url
// or undefined on failure.
export const updateMyAvatar = (token, avatarUrl) => {
  return axios
    .patch(
      `${BASE_URL}/users/me/avatar`,
      { avatar_url: avatarUrl },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data?.data?.avatar_url ?? null)
    // Returns null on failure rather than throwing. This one resolves a bare
    // avatar URL, not an envelope, so there is no { success } field for the
    // caller to read — and the caller (SideNavbarLayout.updateUserAvatar) is a
    // bare async function with no catch. An explicit falsy value is the honest
    // contract here; the eslint rule accepts a returned failure value.
    .catch((err) => {
      captureException(err);
      return null;
    });
};

/**
 * The signed-in person changes their OWN name (PATCH /users/me/name).
 *
 * The staff Profile form used PUT /users/:id, which is for managing other
 * staff and finds no row for a tenant owner: every save was a silent 404
 * (2026-10-09). Resolves `{ ok: true }` or `{ ok: false, message }`; never
 * throws, so the form can say what happened.
 */
export const updateMyName = (token, { firstName, lastName }) => {
  return axios
    .patch(
      `${BASE_URL}/users/me/name`,
      { first_name: firstName, last_name: lastName },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => ({ ok: res?.data?.success === true, message: res?.data?.message || null }))
    .catch((err) => {
      captureException(err);
      return {
        ok: false,
        message: err?.response?.data?.message || "Your name was not saved. Please try again.",
      };
    });
};

export const changePassword = (newForm) => {
  const token = getAuthToken();
  return axios
    .post(`${BASE_URL}/auth/reset-password`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      return err?.response?.data;
    });
};

// GET
export const getStaffUsers = (token) => {
  return axios
    .get(`${BASE_URL}/users/staff`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// PUT
export const updateEmailAdmin = (newForm) => {
  return axios
    .put(`${BASE_URL}/auth/update-email-admin`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${getAuthToken()}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      if (err) return err?.response?.data;
    });
};

// PUT
export const updateEmailSelf = (token, newForm) => {
  return axios
    .put(`${BASE_URL}/auth/update-email`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      if (err) return err?.response;
    });
};

// Search for tenants (public endpoint)
export const searchTenants = async (query) => {
  try {
    const response = await axios.get(`${BASE_URL}/auth/search-tenants`, {
      params: { q: query },
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response?.data?.data || [];
  } catch (err) {

    return [];
  }
};

// GET tasks assigned to a specific user
export const getUserTasks = (token, userId, options = {}) => {
  const { status, includeCompleted } = options;
  const params = {};
  if (status) params.status = status;
  if (includeCompleted !== undefined) params.includeCompleted = includeCompleted;

  return axios
    .get(`${BASE_URL}/users/${userId}/tasks`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params,
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      return err?.response?.data;
    });
};

// GET activity logs for a specific user
export const getUserActivity = (token, userId, options = {}) => {
  const { types, limit, offset } = options;
  const params = {};
  if (types && types.length > 0) params.types = types.join(",");
  if (limit) params.limit = limit;
  if (offset) params.offset = offset;

  return axios
    .get(`${BASE_URL}/users/${userId}/activity`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params,
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      return err?.response?.data;
    });
};

// GET - Get tenant owners (users with tenant access via central auth)
export const getTenantOwners = () => {
  const token = getAuthToken();

  return axios
    .get(`${BASE_URL}/users/tenant-owners`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      return { success: false, error: err?.message, data: [] };
    });
};
